import { useEffect, useState } from "react"
import { Card, Button, Form, Row, Col, Badge, Spinner, Alert } from "react-bootstrap"
import { FaUtensils, FaPercent, FaHotel, FaSave, FaEdit, FaTimes, FaInfoCircle } from "react-icons/fa"
import { toast } from "react-toastify"
import { getBaseUrl } from "../../../../Redux/Config/AppConfig"
import { useHotel } from "../../../../Common/HotelContext"

const RestaurantSettingsManager = () => {
  const baseUrl = getBaseUrl()

  // Use HotelContext
  const { selectedHotel } = useHotel()

  const [settings, setSettings] = useState({
    restaurant_service_charge: 10, // Default 10% as per India
    restaurant_tax: 5, // Default 5% GST as per India
    restaurant_discount: 0, // Default 0%
    room_reservation_tax: 12, // Default 12% GST as per India
  })
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings()
  }, [selectedHotel])

  const fetchSettings = async () => {
    setLoading(true)
    try {
      if (selectedHotel) {
        const token = localStorage.getItem("token")
        const res = await fetch(`${baseUrl}/restaurant-settings?hotel_id=${selectedHotel}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await res.json()
        if (data.data) {
          setSettings({
            restaurant_service_charge: data.data.restaurant_service_charge || 10,
            restaurant_tax: data.data.restaurant_tax || 5,
            restaurant_discount: data.data.restaurant_discount || 0,
            room_reservation_tax: data.data.room_reservation_tax || 12,
          })
        }
      }
    } catch (err) {
      // Keep default values if API fails
    }
    setLoading(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setSettings({ ...settings, [name]: parseFloat(value) || 0 })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      const token = localStorage.getItem("token")
      const res = await fetch(`${baseUrl}/restaurant-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...settings, hotel_id: selectedHotel }),
      })

      if (res.ok) {
        toast.success("Settings saved successfully!")
        setEditing(false)
      } else {
        toast.error("Failed to save settings")
      }
    } catch (err) {
      toast.error("Failed to save settings")
    }
    setSaving(false)
  }

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setEditing(false)
    fetchSettings() // Reset to original values
  }

  const calculateExampleBill = () => {
    const baseAmount = 1000
    const serviceCharge = (baseAmount * settings.restaurant_service_charge) / 100
    const discount = (baseAmount * settings.restaurant_discount) / 100
    const taxableAmount = baseAmount + serviceCharge - discount
    const tax = (taxableAmount * settings.restaurant_tax) / 100
    const total = taxableAmount + tax

    return {
      baseAmount,
      serviceCharge,
      discount,
      taxableAmount,
      tax,
      total,
    }
  }

  const example = calculateExampleBill()

  return (
    <div className="container-fluid px-4 py-4" style={{ maxWidth: "1200px", position: "relative" }}>
      {/* Loader Overlay */}
      {loading && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(255,255,255,0.7)",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Spinner animation="border" variant="primary" style={{ width: 60, height: 60 }} />
          <p className="mt-3 text-primary fs-4">Loading settings...</p>
        </div>
      )}
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold text-primary mb-3">Restaurant & Room Settings</h1>
        <p className="lead text-muted">Configure service charges, taxes, and discounts for your hotel</p>
      </div>

      {/* Settings Form */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0 d-flex align-items-center">
            <FaUtensils className="me-2" />
            {editing ? "Edit Settings" : "Current Settings"}
          </h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            {editing 
              ? "Update the restaurant and room reservation settings below" 
              : "Current configuration for service charges, taxes, and discounts"
            }
          </p>
          
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaPercent className="me-2 text-primary" />
                    Restaurant Service Charge (%)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="restaurant_service_charge"
                    value={settings.restaurant_service_charge}
                    onChange={handleChange}
                    placeholder="10"
                    min="0"
                    max="50"
                    step="0.01"
                    disabled={!editing}
                    className="form-control-lg"
                  />
                  <Form.Text className="text-muted">Default: 10% (as per India standards)</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaPercent className="me-2 text-success" />
                    Food/Restaurant Tax (%)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="restaurant_tax"
                    value={settings.restaurant_tax}
                    onChange={handleChange}
                    placeholder="5"
                    min="0"
                    max="50"
                    step="0.01"
                    disabled={!editing}
                    className="form-control-lg"
                  />
                  <Form.Text className="text-muted">Default: 5% GST (as per India standards)</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaPercent className="me-2 text-warning" />
                    Food/Restaurant Discount (%)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="restaurant_discount"
                    value={settings.restaurant_discount}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={!editing}
                    className="form-control-lg"
                  />
                  <Form.Text className="text-muted">Default: 0% (no discount)</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaHotel className="me-2 text-info" />
                    Room Reservation Tax (%)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="room_reservation_tax"
                    value={settings.room_reservation_tax}
                    onChange={handleChange}
                    placeholder="12"
                    min="0"
                    max="50"
                    step="0.01"
                    disabled={!editing}
                    className="form-control-lg"
                  />
                  <Form.Text className="text-muted">Default: 12% GST (as per India standards)</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            {editing ? (
              <div className="d-flex gap-2">
                <Button type="submit" disabled={saving} variant="primary" size="lg" className="flex-fill">
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Save Settings
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline-secondary" onClick={handleCancel} size="lg">
                  <FaTimes className="me-2" />
                  Cancel
                </Button>
              </div>
            ) : (
              <Button type="button" variant="outline-primary" onClick={handleEdit} size="lg">
                <FaEdit className="me-2" />
                Edit Settings
              </Button>
            )}
          </Form>
        </Card.Body>
      </Card>

      {/* Example Bill Calculation */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0 d-flex align-items-center">
            <FaInfoCircle className="me-2 text-info" />
            Example Bill Calculation
          </h5>
          <small className="text-muted">Sample calculation for ₹1,000 restaurant bill</small>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="border rounded p-3 bg-light">
                <h6 className="text-primary mb-3">Restaurant Bill Breakdown</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span>Base Amount:</span>
                  <span className="fw-bold">₹{example.baseAmount.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Service Charge ({settings.restaurant_service_charge}%):</span>
                  <span className="fw-bold text-primary">₹{example.serviceCharge.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Discount ({settings.restaurant_discount}%):</span>
                  <span className="fw-bold text-success">-₹{example.discount.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Taxable Amount:</span>
                  <span className="fw-bold">₹{example.taxableAmount.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Tax ({settings.restaurant_tax}%):</span>
                  <span className="fw-bold text-warning">₹{example.tax.toFixed(2)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <span className="h5 mb-0">Total:</span>
                  <span className="h5 mb-0 text-danger fw-bold">₹{example.total.toFixed(2)}</span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="border rounded p-3 bg-light">
                <h6 className="text-info mb-3">Room Reservation Tax</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span>Room Rate:</span>
                  <span className="fw-bold">₹5,000</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Tax ({settings.room_reservation_tax}%):</span>
                  <span className="fw-bold text-warning">₹{(5000 * settings.room_reservation_tax / 100).toFixed(2)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <span className="h5 mb-0">Total:</span>
                  <span className="h5 mb-0 text-danger fw-bold">₹{(5000 * (1 + settings.room_reservation_tax / 100)).toFixed(2)}</span>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Current Settings Summary */}
      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Settings Summary</h5>
          <small className="text-muted">Current configuration overview</small>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Loading settings...</p>
            </div>
          ) : (
            <Row>
              <Col md={3}>
                <div className="text-center p-3 border rounded">
                  <FaUtensils size={32} className="text-primary mb-2" />
                  <h4 className="text-primary fw-bold">{settings.restaurant_service_charge}%</h4>
                  <p className="text-muted mb-0">Service Charge</p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center p-3 border rounded">
                  <FaPercent size={32} className="text-success mb-2" />
                  <h4 className="text-success fw-bold">{settings.restaurant_tax}%</h4>
                  <p className="text-muted mb-0">Restaurant Tax</p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center p-3 border rounded">
                  <FaPercent size={32} className="text-warning mb-2" />
                  <h4 className="text-warning fw-bold">{settings.restaurant_discount}%</h4>
                  <p className="text-muted mb-0">Discount</p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center p-3 border rounded">
                  <FaHotel size={32} className="text-info mb-2" />
                  <h4 className="text-info fw-bold">{settings.room_reservation_tax}%</h4>
                  <p className="text-muted mb-0">Room Tax</p>
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default RestaurantSettingsManager 